import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LearnerUpgrade — Phase 1 (Feb 6, 2026): single-purpose conversion page.
 *
 * Replaces the cluttered /pricing redirect for in-app upgrade CTAs.
 * Layout is strict: title · 3 benefits · price · single Upgrade now CTA.
 * No nav, no FAQ, no testimonials, no comparison tables.
 */
const HEADLINE_BENEFITS = [
  "Full access to every lesson, video and confidence check",
  "Personalised next-lesson plan guided by your progress",
  "Advanced modules + community access for tougher topics",
];

export default function LearnerUpgrade() {
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/plans`);
        const premium = (r.data?.learner_plans || []).find((p) => p.id === "premium");
        if (alive) setPlan(premium || null);
      } catch {
        if (alive) setPlan(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const upgrade = async () => {
    setSubmitting(true);
    try {
      // Hand off to the existing checkout flow on /pricing — Phase 1 does
      // NOT change the payment plumbing, only the UX surface.
      nav("/pricing?upgrade=1&from=upgrade-page");
    } catch (e) {
      toast.error("Couldn't start checkout — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="learner-upgrade-page"
      className="min-h-dvh no-overflow-x bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950 text-white"
    >
      <header className="sticky top-0 z-30">
        <div className="max-w-md mx-auto px-4 h-14 flex items-center">
          <button
            data-testid="upgrade-back-btn"
            onClick={() => nav(-1)}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 pt-6 pb-24">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1 mb-6">
          <Sparkles className="w-3.5 h-3.5 text-amber-300" />
          <span className="text-[11px] font-bold tracking-wide text-amber-200 uppercase">Premium</span>
        </div>

        <h1
          data-testid="upgrade-title"
          className="text-3xl sm:text-4xl font-extrabold leading-tight mb-3"
        >
          Unlock the rest<br />of your learning.
        </h1>

        <ul data-testid="upgrade-benefits" className="space-y-3 mt-8 mb-10">
          {HEADLINE_BENEFITS.map((b, i) => (
            <li
              key={i}
              data-testid={`upgrade-benefit-${i}`}
              className="flex items-start gap-3"
            >
              <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-400 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-emerald-950 stroke-[3]" />
              </span>
              <span className="text-base leading-snug text-white/90">{b}</span>
            </li>
          ))}
        </ul>

        <div data-testid="upgrade-price-block" className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-5 mb-6">
          {loading ? (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="w-5 h-5 animate-spin text-white/40" />
            </div>
          ) : plan ? (
            <div className="flex items-baseline gap-1">
              <span data-testid="upgrade-price" className="text-4xl font-extrabold">
                £{(plan.price || 0).toFixed(2)}
              </span>
              <span className="text-base text-white/60 ml-1">/ {plan.interval || "month"}</span>
            </div>
          ) : (
            <p className="text-sm text-white/60">Pricing unavailable — please retry shortly.</p>
          )}
          <p className="text-[11px] text-white/50 mt-1">Cancel anytime. No tie-in.</p>
        </div>

        <button
          data-testid="upgrade-cta"
          onClick={upgrade}
          disabled={submitting || loading || !plan}
          className="w-full h-14 rounded-2xl bg-amber-400 hover:bg-amber-300 text-slate-950 text-base font-extrabold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Upgrade now"}
        </button>
      </main>
    </div>
  );
}
